import { useEffect, useId, useMemo, useState } from 'react';
import { api } from '../lib/api';
import type { ApiResponse } from '../lib/types';
import { Badge, Button, Card, Input } from './ui';

interface Destination {
  id: string;
  name: string;
  type: string;
  client_name?: string;
  status?: string;
  feed_url?: string;
  tracking_code?: string;
}

interface DestinationItem {
  id?: string;
  itemId?: string;
  name?: string;
  sku?: string;
  images?: string | string[];
  image_link?: string;
  price?: number | string | null;
  salesPrice?: number | string | null;
  currency?: string;
  inStock?: boolean;
  url?: string;
  urlclean?: string;
  urlwithtracking?: string;
  tracking_code?: string;
}

interface DestinationPreviewData extends Destination {
  items: DestinationItem[];
  items_count: number;
}

const normalize = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

function safeUrl(value?: string): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    return ['https:', 'http:'].includes(url.protocol) ? url.href : undefined;
  } catch { return undefined; }
}

function firstImage(item: DestinationItem): string | undefined {
  let images = item.images || item.image_link;
  if (typeof images === 'string' && images.trim().startsWith('[')) {
    try { images = JSON.parse(images); } catch { return undefined; }
  }
  return safeUrl(Array.isArray(images) ? images.find((entry) => typeof entry === 'string') : images);
}

function ProductImage({ src }: { src?: string }) {
  const [failed, setFailed] = useState(false);
  return (
    <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-surface text-center">
      {src && !failed
        ? <img src={src} alt="" loading="lazy" referrerPolicy="no-referrer" className="h-full w-full object-contain" onError={() => setFailed(true)} />
        : <span className="px-1 text-[10px] text-slate-400">No image</span>}
    </div>
  );
}

function priceLabel(item: DestinationItem): string | null {
  const raw = item.salesPrice ?? item.price;
  if (raw == null || raw === '' || !Number.isFinite(Number(raw))) return null;
  const amount = Number(raw);
  try {
    return new Intl.NumberFormat('en-US', item.currency ? { style: 'currency', currency: item.currency } : { maximumFractionDigits: 2 }).format(amount);
  } catch { return `${amount} ${item.currency || ''}`.trim(); }
}

function PreviewUrl({ label, value }: { label: string; value?: string }) {
  const href = safeUrl(value);
  return (
    <div className="min-w-0">
      <dt className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className="break-all text-xs leading-relaxed">
        {href ? <a href={href} target="_blank" rel="noopener noreferrer" className="text-brand-600 dark:text-brand-500 hover:underline">{value}</a>
          : <span className="text-slate-500">{value || 'Not available'}</span>}
      </dd>
    </div>
  );
}

function DestinationPreview({ id, selected, onSelect }: { id: string; selected: boolean; onSelect: (id: string) => void }) {
  const [data, setData] = useState<DestinationPreviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    api.get<ApiResponse<DestinationPreviewData>>(`/destinations/get?id=${encodeURIComponent(id)}&preview=true`)
      .then((response) => {
        if (!response.success || !response.data || response.data.id !== id || !Array.isArray(response.data.items)) {
          throw new Error(response.error || response.message || 'Failed to load destination preview.');
        }
        if (active) setData(response.data);
      })
      .catch((err: unknown) => {
        if (active) setError(err instanceof Error ? err.message : 'Failed to load destination preview.');
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [id, retry]);

  if (loading) return <div role="status" className="p-10 text-center text-sm text-slate-400">Loading products and tracking…</div>;
  if (error || !data) return (
    <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
      <p>{error || 'Destination preview is unavailable.'}</p>
      <Button type="button" variant="outline" className="mt-3" onClick={() => setRetry((value) => value + 1)}>Retry preview</Button>
    </div>
  );

  const items = data.items.slice(0, 10);
  const total = Number(data.items_count);
  const hasTotal = Number.isFinite(total) && total >= items.length;

  return (
    <div className="min-w-0 space-y-4">
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row">
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <Badge tone="blue">{data.type === 'feed' ? 'Feed' : data.type === 'links' ? 'Links' : data.type}</Badge>
            {data.status && <Badge tone={data.status === 'active' ? 'green' : 'neutral'}>{data.status}</Badge>}
            {selected && <Badge tone="brand">Selected</Badge>}
          </div>
          <h3 className="break-words text-lg font-semibold text-ink-900">{data.name}</h3>
          <p className="mt-1 break-all font-mono text-[10px] text-slate-400">{data.id}</p>
        </div>
        <Button type="button" disabled={selected} onClick={() => onSelect(data.id)}>
          {selected ? 'Selected for this campaign' : 'Use this destination'}
        </Button>
      </div>

      <dl className="space-y-3 rounded-lg border border-border bg-surface-muted p-3">
        {data.feed_url && <PreviewUrl label="Feed URL" value={data.feed_url} />}
        <div>
          <dt className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Set tracking</dt>
          <dd className="break-all font-mono text-xs text-slate-600">{data.tracking_code || 'No set tracking configured'}</dd>
        </div>
      </dl>

      <div>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-sm font-semibold text-ink-900">Products &amp; tracking</h4>
          <span className="text-xs text-slate-500">{hasTotal ? `${items.length} of ${total.toLocaleString('en-US')} items` : `${items.length} preview items`}</span>
        </div>
        {items.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-slate-500">No items in this destination set.</p>
        ) : (
          <>
            <p className="mb-3 text-xs text-slate-400">
              {hasTotal && total < 10 ? 'This set has fewer than 10 items; all available items are shown. ' : 'Preview of the first 10 items. '}
              Check the stored tracking URL for each item before applying the selection.
            </p>
            <ol aria-label="Destination preview items" className="max-h-[620px] overflow-y-auto rounded-lg border border-border divide-y divide-border">
              {items.map((item, index) => {
                const image = firstImage(item);
                const price = priceLabel(item);
                return (
                  <li key={item.id || `${item.itemId || item.sku || 'item'}-${index}`} className="p-3">
                    <div className="flex min-w-0 gap-3">
                      <ProductImage key={image || 'no-image'} src={image} />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-start justify-between gap-1">
                          <p className="min-w-0 break-words text-sm font-medium text-ink-900"><span className="mr-2 text-xs text-slate-400">{index + 1}.</span>{item.name || item.sku || item.itemId || 'Untitled item'}</p>
                          {price && <span className="text-xs font-medium text-slate-600">{price}</span>}
                        </div>
                        <p className="mt-1 break-all text-[11px] text-slate-500">SKU: {item.sku || '—'}{item.itemId ? ` · ID: ${item.itemId}` : ''}</p>
                        {typeof item.inStock === 'boolean' && <p className={`mt-1 text-[11px] ${item.inStock ? 'text-emerald-700' : 'text-amber-700'}`}>{item.inStock ? 'In stock' : 'Out of stock'}</p>}
                      </div>
                    </div>
                    <dl className="mt-3 grid min-w-0 gap-2">
                      <PreviewUrl label="Product URL" value={item.url || item.urlclean} />
                      <PreviewUrl label="URL with tracking" value={item.urlwithtracking} />
                      {item.tracking_code && <div><dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Item tracking</dt><dd className="mt-0.5 break-all font-mono text-xs text-slate-600">{item.tracking_code}</dd></div>}
                    </dl>
                  </li>
                );
              })}
            </ol>
          </>
        )}
      </div>
    </div>
  );
}

export function DestinationSetPicker({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  const searchId = useId();
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  const [search, setSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(8);
  const [previewId, setPreviewId] = useState(value);

  useEffect(() => { setPreviewId(value); }, [value]);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    api.get<ApiResponse<Destination[]>>('/destinations/list?withCounts=false')
      .then((response) => {
        if (!response.success || !Array.isArray(response.data)) throw new Error(response.error || response.message || 'Failed to load destination sets.');
        if (active) setDestinations(response.data);
      })
      .catch((err: unknown) => { if (active) setError(err instanceof Error ? err.message : 'Failed to load destination sets.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [retry]);

  const results = useMemo(() => {
    const terms = normalize(search).trim().split(/\s+/).filter(Boolean);
    return destinations.filter((destination) => {
      const text = normalize(`${destination.name} ${destination.id} ${destination.type} ${destination.client_name || ''} ${destination.feed_url || ''}`);
      return terms.every((term) => text.includes(term));
    });
  }, [destinations, search]);
  const selected = destinations.find((destination) => destination.id === value);

  return (
    <section aria-label="Destination Set" className="min-w-0 mb-5">
      <Card className="min-w-0 p-5">
        <div className="mb-4">
          <h2 className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Destination Set</h2>
          <p className="mt-1 text-sm text-slate-500">Search a destination, check its products and tracking, then apply it to this campaign.</p>
        </div>
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-brand-100 bg-brand-50 px-4 py-3">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-brand-600 dark:text-brand-500">Selected for this campaign</p>
            <p className="mt-1 break-words text-sm font-semibold text-ink-900">{value ? selected?.name || value : 'No destination set selected'}</p>
            {selected && <p className="mt-0.5 break-all text-[11px] text-slate-500">{selected.client_name ? `${selected.client_name} · ` : ''}{selected.id}</p>}
          </div>
          {value && <Button type="button" variant="outline" onClick={() => { onChange(''); setPreviewId(''); }}>Remove selection</Button>}
        </div>
        <div className="grid min-w-0 grid-cols-1 gap-5 lg:grid-cols-[minmax(240px,0.8fr)_minmax(0,2fr)]">
          <div className="min-w-0">
            <label htmlFor={searchId} className="mb-1.5 block text-xs font-semibold text-slate-600">Search destination sets</label>
            <Input id={searchId} type="search" value={search} onChange={(event) => { setSearch(event.target.value); setVisibleCount(8); }} placeholder="Name, ID, client or feed URL…" />
            {loading ? <p role="status" className="py-6 text-center text-sm text-slate-400">Loading destination sets…</p>
              : error ? <div role="alert" className="mt-3 rounded-lg bg-rose-50 p-3 text-sm text-rose-700"><p>{error}</p><Button type="button" variant="outline" className="mt-2" onClick={() => setRetry((count) => count + 1)}>Retry destinations</Button></div>
              : <>
                <p role="status" className="my-2 text-[11px] text-slate-400">{results.length} destination{results.length === 1 ? '' : 's'} found</p>
                <div className="max-h-[520px] space-y-2 overflow-y-auto">
                  {results.slice(0, visibleCount).map((destination) => (
                    <button key={destination.id} type="button" aria-label={`Preview ${destination.name}`} aria-pressed={previewId === destination.id}
                      onClick={() => setPreviewId(destination.id)}
                      className={`w-full rounded-lg border p-3 text-left transition-colors focus-visible:outline-2 focus-visible:outline-brand-500 ${previewId === destination.id ? 'border-brand-500 bg-brand-50' : 'border-border hover:bg-surface-muted'}`}>
                      <span className="flex items-start justify-between gap-2"><span className="min-w-0 break-words text-sm font-semibold text-ink-900">{destination.name}</span>{value === destination.id && <span className="shrink-0 text-[10px] font-semibold text-brand-600">Selected</span>}</span>
                      <span className="mt-1 block break-words text-xs text-slate-500">{destination.type}{destination.client_name ? ` · ${destination.client_name}` : ''}</span>
                      <span className="mt-1 block break-all font-mono text-[10px] text-slate-400">{destination.id}</span>
                    </button>
                  ))}
                </div>
                {results.length === 0 && <p className="py-6 text-center text-sm text-slate-500">{search.trim() ? 'No destinations match your search.' : 'No destination sets available.'}</p>}
                {results.length > visibleCount && <Button type="button" variant="outline" className="mt-3" onClick={() => setVisibleCount((count) => count + 8)}>Show more destinations</Button>}
              </>}
          </div>
          <div className="min-w-0 border-t border-border pt-5 lg:border-t-0 lg:border-l lg:pl-5 lg:pt-0">
            {previewId ? <DestinationPreview key={previewId} id={previewId} selected={previewId === value} onSelect={onChange} />
              : <div className="flex min-h-52 items-center justify-center rounded-lg border border-dashed border-border px-6 text-center text-sm text-slate-400">Choose a destination from the results to preview its products and tracking.</div>}
          </div>
        </div>
        <p className="mt-4 border-t border-border pt-3 text-[11px] text-slate-400">The selection is saved when you save the campaign.</p>
      </Card>
    </section>
  );
}
